import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { ChatMessage as ChatMessageType, DetailedOrder, Repair } from "@/hooks/useJewelryAdvisor";
import { QuickReplyButtons } from "./QuickReplyButtons";
import { ProductResults } from "./ProductResults";
import { ExternalLink, Package, AlertCircle, Phone, MapPin, Clock, Check, Circle, Hash, CreditCard, Truck, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { format } from "date-fns";
import { he } from "date-fns/locale";
import whatsappIcon from "@/assets/whatsapp-icon.png";

interface AccessibilitySettings {
  highContrast: boolean;
  largeText: boolean;
  highlightLinks: boolean;
}

interface ChatMessageProps {
  message: ChatMessageType;
  onOptionSelect: (value: string) => void;
  isLatest: boolean;
  accessibility?: AccessibilitySettings;
}

const statusLabels: Record<string, string> = {
  UNFULFILLED: "ממתין לטיפול",
  FULFILLED: "נשלח",
  PARTIALLY_FULFILLED: "נשלח חלקית",
  PENDING: "ממתין",
  PAID: "שולם",
  pending: "ממתין",
  in_repair: "בתיקון",
  completed: "הושלם",
  shipped: "נשלח",
  delivered: "נמסר",
  cancelled: "בוטל",
  waiting_in_store: "ממתין בחנות",
};

const STORE_INFO = {
  address: "הארז 58, באר שבע",
  phone: "079-5575576",
  whatsapp: "054-5267564",
  email: "info@alicejewelry.net",
  hours: [
    { day: "ראשון", time: "10:00-19:00" },
    { day: "שני", time: "10:00-19:00" },
    { day: "שלישי", time: "09:45-14:00" },
    { day: "רביעי", time: "10:00-19:00" },
    { day: "חמישי", time: "10:00-19:00" },
    { day: "שישי וערבי חג", time: "09:45-14:00" },
    { day: "שבתות וחגים", time: "סגור" },
  ],
  mapUrl: "https://www.google.com/maps/search/?api=1&query=הארז+58+באר+שבע",
  wazeUrl: "https://waze.com/ul?q=הארז%2058%20באר%20שבע",
};

function OrderTimeline({ timeline, highContrast, largeText }: { timeline: { status: string; date: string | null; completed: boolean }[]; highContrast?: boolean; largeText?: boolean }) {
  return (
    <div className="space-y-2 my-3">
      {timeline.map((step, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <div className={cn(
            "flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center",
            step.completed 
              ? highContrast ? "bg-yellow-400 text-black" : "bg-primary text-primary-foreground"
              : highContrast ? "bg-gray-600" : "bg-muted"
          )}>
            {step.completed ? <Check className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
          </div>
          <div className="flex-1">
            <span className={cn(
              "font-medium",
              largeText ? "text-sm" : "text-xs",
              step.completed ? "" : highContrast ? "text-gray-400" : "text-muted-foreground"
            )}>{step.status}</span>
            {step.date && (
              <span className={cn(
                "mr-2",
                largeText ? "text-xs" : "text-[10px]",
                highContrast ? "text-gray-400" : "text-muted-foreground"
              )}>
                {format(new Date(step.date), "dd/MM/yyyy HH:mm", { locale: he })}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailedOrderView({ order, highContrast, largeText, highlightLinks }: { order: DetailedOrder; highContrast?: boolean; largeText?: boolean; highlightLinks?: boolean }) {
  return (
    <div className={cn("mt-3 space-y-3", largeText ? "text-sm" : "text-xs")}>
      {/* Order header */}
      <div className={cn(
        "rounded-lg p-3",
        highContrast ? "bg-white/10" : "bg-background/50"
      )}>
        <div className="flex justify-between items-center mb-2">
          <span className="font-bold flex items-center gap-1">
            <Hash className="h-3 w-3" />
            {order.orderNumber}
          </span>
          <span className={cn("font-bold", highContrast ? "text-yellow-400" : "text-primary")}>
            ₪{parseFloat(order.total).toFixed(0)}
          </span>
        </div>
        <div className={cn("flex gap-4 flex-wrap", highContrast ? "text-gray-300" : "text-muted-foreground")}>
          <span>{format(new Date(order.createdAt), "dd/MM/yyyy", { locale: he })}</span>
          <span className={highContrast ? "text-yellow-400" : "text-primary"}>{statusLabels[order.financialStatus] || order.financialStatus}</span>
          <span>{statusLabels[order.fulfillmentStatus] || order.fulfillmentStatus}</span>
        </div>
      </div>

      {/* Timeline */}
      <div className={cn(
        "rounded-lg p-3",
        highContrast ? "bg-white/10" : "bg-background/50"
      )}>
        <p className={cn("font-medium mb-2", highContrast ? "text-white" : "text-foreground")}>מעקב משלוח:</p>
        <OrderTimeline timeline={order.timeline} highContrast={highContrast} largeText={largeText} />
      </div>

      {/* Items */}
      <div className={cn(
        "rounded-lg p-3",
        highContrast ? "bg-white/10" : "bg-background/50"
      )}>
        <p className={cn("font-medium mb-2", highContrast ? "text-white" : "text-foreground")}>פריטים:</p>
        {order.items.map((item, idx) => (
          <div key={idx} className="flex gap-2 py-1 border-b border-border/30 last:border-0">
            {item.imageUrl && (
              <img src={item.imageUrl} alt={item.title} className="w-10 h-10 object-cover rounded" />
            )}
            <div className="flex-1">
              <p className="font-medium">{item.title}</p>
              {item.variantTitle && item.variantTitle !== "Default Title" && (
                <p className={highContrast ? "text-gray-400" : "text-muted-foreground"}>{item.variantTitle}</p>
              )}
              <p>כמות: {item.quantity}</p>
            </div>
            {item.price && (
              <span className={cn("font-medium", highContrast ? "text-yellow-400" : "text-primary")}>
                ₪{parseFloat(item.price).toFixed(0)}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Shipping address */}
      {order.shippingAddress && (
        <div className={cn(
          "rounded-lg p-3",
          highContrast ? "bg-white/10" : "bg-background/50"
        )}>
          <p className={cn("font-medium mb-1 flex items-center gap-1", highContrast ? "text-white" : "text-foreground")}>
            <Truck className="h-3 w-3" /> כתובת למשלוח:
          </p>
          <p>{order.shippingAddress.name}</p>
          <p>{order.shippingAddress.address1}</p>
          {order.shippingAddress.address2 && <p>{order.shippingAddress.address2}</p>}
          <p>{order.shippingAddress.city} {order.shippingAddress.zip}</p>
        </div>
      )}

      {/* Tracking info */}
      {order.fulfillments.length > 0 && order.fulfillments[0].trackingInfo.length > 0 && (
        <div className={cn(
          "rounded-lg p-3",
          highContrast ? "bg-white/10" : "bg-background/50"
        )}>
          <p className={cn("font-medium mb-1", highContrast ? "text-white" : "text-foreground")}>מעקב משלוח:</p>
          {order.fulfillments[0].trackingInfo.map((track, idx) => (
            <a 
              key={idx}
              href={track.url || "#"} 
              target="_blank" 
              rel="noopener noreferrer"
              className={cn(
                "flex items-center gap-1 hover:underline",
                highlightLinks 
                  ? "bg-yellow-300 text-black px-1 rounded font-bold" 
                  : highContrast ? "text-yellow-400" : "text-primary"
              )}
            >
              <Package className="h-3 w-3" />
              {track.company}: {track.number}
              <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      )}

      {/* Price breakdown */}
      <div className={cn(
        "rounded-lg p-3",
        highContrast ? "bg-white/10" : "bg-background/50"
      )}>
        <div className="space-y-1">
          <div className="flex justify-between">
            <span>סכום ביניים:</span>
            <span>₪{parseFloat(order.subtotal).toFixed(0)}</span>
          </div>
          <div className="flex justify-between">
            <span>משלוח:</span>
            <span>₪{parseFloat(order.shipping).toFixed(0)}</span>
          </div>
          {parseFloat(order.tax) > 0 && (
            <div className="flex justify-between">
              <span>מע"מ:</span>
              <span>₪{parseFloat(order.tax).toFixed(0)}</span>
            </div>
          )}
          <div className={cn("flex justify-between font-bold pt-1 border-t", highContrast ? "border-white/20" : "border-border")}>
            <span>סה"כ:</span>
            <span className={highContrast ? "text-yellow-400" : "text-primary"}>₪{parseFloat(order.total).toFixed(0)}</span>
          </div>
        </div>
      </div>

      {/* Store info */}
      <div className={cn(
        "rounded-lg p-3",
        highContrast ? "bg-white/10" : "bg-background/50"
      )}>
        <p className={cn("font-medium mb-2 flex items-center gap-1", highContrast ? "text-white" : "text-foreground")}>
          <MapPin className="h-3 w-3" /> החנות שלנו:
        </p>
        <p className="mb-2">{STORE_INFO.address}</p>
        <a 
          href={STORE_INFO.mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "flex items-center gap-1 mb-3 hover:underline",
            highlightLinks 
              ? "bg-yellow-300 text-black px-1 rounded font-bold" 
              : highContrast ? "text-yellow-400" : "text-primary"
          )}
        >
          <MapPin className="h-3 w-3" />
          פתח במפה
          <ExternalLink className="h-3 w-3" />
        </a>
        
        <p className={cn("font-medium mb-1 flex items-center gap-1", highContrast ? "text-white" : "text-foreground")}>
          <Clock className="h-3 w-3" /> שעות פתיחה:
        </p>
        {STORE_INFO.hours.map((h, idx) => (
          <div key={idx} className="flex justify-between">
            <span>{h.day}</span>
            <span>{h.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailedRepairView({ repair, highContrast, largeText, highlightLinks }: { repair: Repair; highContrast?: boolean; largeText?: boolean; highlightLinks?: boolean }) {
  const orderNum = repair.orderNumber || repair.order_number;
  const createdDate = repair.createdAt || repair.created_at;
  const statusLabel = repair.statusLabel || statusLabels[repair.status] || repair.status;
  const timeline = repair.timeline || [];
  
  return (
    <div className={cn("mt-3 space-y-3", largeText ? "text-sm" : "text-xs")}>
      {/* Header */}
      <div className={cn(
        "rounded-lg p-3",
        highContrast ? "bg-white/10" : "bg-background/50"
      )}>
        <div className="flex justify-between items-center mb-2">
          <span className="font-bold">תיקון #{orderNum}</span>
          <span className={cn("px-2 py-0.5 rounded", highContrast ? "bg-yellow-400 text-black" : "bg-primary/20 text-primary")}>
            {statusLabel}
          </span>
        </div>
        <p className="font-medium">{repair.title}</p>
        {repair.description && (
          <p className={highContrast ? "text-gray-300" : "text-muted-foreground"}>{repair.description}</p>
        )}
        {createdDate && (
          <p className={cn("mt-2", highContrast ? "text-gray-300" : "text-muted-foreground")}>
            תאריך קבלה: {format(new Date(createdDate), "dd/MM/yyyy", { locale: he })}
          </p>
        )}
      </div>

      {/* Timeline */}
      {timeline.length > 0 && (
        <div className={cn(
          "rounded-lg p-3",
          highContrast ? "bg-white/10" : "bg-background/50"
        )}>
          <p className={cn("font-medium mb-2", highContrast ? "text-white" : "text-foreground")}>מעקב תיקון:</p>
          <OrderTimeline timeline={timeline} highContrast={highContrast} largeText={largeText} />
        </div>
      )}

      {/* Store info */}
      <div className={cn(
        "rounded-lg p-3",
        highContrast ? "bg-white/10" : "bg-background/50"
      )}>
        <p className={cn("font-medium mb-2 flex items-center gap-1", highContrast ? "text-white" : "text-foreground")}>
          <MapPin className="h-3 w-3" /> החנות שלנו:
        </p>
        <p className="mb-2">{STORE_INFO.address}</p>
        <div className="flex gap-3 mb-3 flex-wrap">
          <a 
            href={STORE_INFO.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center gap-1 hover:underline",
              highlightLinks 
                ? "bg-yellow-300 text-black px-1 rounded font-bold" 
                : highContrast ? "text-yellow-400" : "text-primary"
            )}
          >
            <MapPin className="h-3 w-3" />
            פתח במפה
            <ExternalLink className="h-3 w-3" />
          </a>
          <a 
            href={STORE_INFO.wazeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center gap-1 hover:underline",
              highlightLinks 
                ? "bg-yellow-300 text-black px-1 rounded font-bold" 
                : highContrast ? "text-yellow-400" : "text-primary"
            )}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" role="img" xmlns="http://www.w3.org/2000/svg" className="shrink-0" fill="currentColor">
              <path d="M13.314 1.59c-.225.003-.45.013-.675.03-2.165.155-4.295.924-6.069 2.327-2.194 1.732-3.296 4.325-3.496 7.05h.002c-.093 1.22-.23 2.15-.469 2.63-.238.479-.42.638-1.24.639C.27 14.259-.4 15.612.266 16.482c1.248 1.657 2.902 2.705 4.72 3.364a2.198 2.198 0 00-.033.367 2.198 2.198 0 002.2 2.197 2.198 2.198 0 002.128-1.668c1.307.12 2.607.14 3.824.1.364-.012.73-.045 1.094-.092a2.198 2.198 0 002.127 1.66 2.198 2.198 0 002.2-2.197 2.198 2.198 0 00-.151-.797 12.155 12.155 0 002.303-1.549c2.094-1.807 3.511-4.399 3.302-7.404-.112-1.723-.761-3.298-1.748-4.608-2.143-2.86-5.53-4.309-8.918-4.265zm.366 1.54c.312.008.623.027.933.063 2.48.288 4.842 1.496 6.4 3.577v.001c.829 1.1 1.355 2.386 1.446 3.792v.003c.173 2.477-.965 4.583-2.777 6.147a10.66 10.66 0 01-2.375 1.535 2.198 2.198 0 00-.98-.234 2.198 2.198 0 00-1.934 1.158 9.894 9.894 0 01-1.338.146 27.323 27.323 0 01-3.971-.148 2.198 2.198 0 00-1.932-1.156 2.198 2.198 0 00-1.347.463c-1.626-.553-3.078-1.422-4.155-2.762 1.052-.096 1.916-.6 2.319-1.408.443-.889.53-1.947.625-3.198v-.002c.175-2.391 1.11-4.536 2.92-5.964h.002c1.77-1.402 3.978-2.061 6.164-2.012zm-3.157 4.638c-.688 0-1.252.579-1.252 1.298 0 .72.564 1.297 1.252 1.297.689 0 1.252-.577 1.252-1.297 0-.711-.563-1.298-1.252-1.298zm5.514 0c-.688 0-1.25.579-1.25 1.298-.008.72.554 1.297 1.25 1.297.688 0 1.252-.577 1.252-1.297 0-.711-.564-1.298-1.252-1.298zM9.641 11.78a.72.72 0 00-.588.32.692.692 0 00-.11.54c.345 1.783 2.175 3.129 4.264 3.129h.125c1.056-.032 2.026-.343 2.816-.922.767-.556 1.29-1.316 1.477-2.137a.746.746 0 00-.094-.547.69.69 0 00-.445-.32.714.714 0 00-.867.539c-.22.93-1.299 1.9-2.934 1.94-1.572.046-2.738-.986-2.926-1.956a.72.72 0 00-.718-.586Z"/>
            </svg>
            נווט עם Waze
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        
        <p className={cn("font-medium mb-1 flex items-center gap-1", highContrast ? "text-white" : "text-foreground")}>
          <Clock className="h-3 w-3" /> שעות פתיחה:
        </p>
        {STORE_INFO.hours.map((h, idx) => (
          <div key={idx} className="flex justify-between">
            <span>{h.day}</span>
            <span>{h.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ChatMessage({ message, onOptionSelect, isLatest, accessibility }: ChatMessageProps) {
  const isUser = message.role === "user";
  const navigate = useNavigate();

  // Handle redirects
  useEffect(() => {
    if (message.type === "redirect_contact" && isLatest) {
      setTimeout(() => navigate("/contact"), 1000);
    }
    if (message.type === "redirect_track_order" && isLatest) {
      setTimeout(() => navigate("/track-order"), 1000);
    }
    if (message.type === "redirect_profile_repairs" && isLatest) {
      setTimeout(() => navigate("/profile?tab=repairs"), 1000);
    }
    if (message.type === "open_whatsapp" && message.whatsappUrl && isLatest) {
      setTimeout(() => window.open(message.whatsappUrl, "_blank"), 500);
    }
  }, [message, isLatest, navigate]);

  const highContrast = accessibility?.highContrast;
  const largeText = accessibility?.largeText;
  const highlightLinks = accessibility?.highlightLinks;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "flex w-full gap-2",
        isUser ? "justify-end" : "justify-start"
      )}
    >
      <div
        className={cn(
          "max-w-[85%] rounded-2xl px-4 py-3",
          isUser
            ? highContrast 
              ? "bg-yellow-400 text-black rounded-tl-sm"
              : "bg-primary text-primary-foreground rounded-tl-sm"
            : highContrast
              ? "bg-white/20 text-white rounded-tr-sm"
              : "bg-muted text-foreground rounded-tr-sm",
          message.type === "error" && (highContrast ? "bg-red-900 border border-red-500" : "bg-destructive/10 border border-destructive/20")
        )}
      >
        {/* Error icon */}
        {message.type === "error" && (
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className={cn("h-4 w-4", highContrast ? "text-red-400" : "text-destructive")} />
          </div>
        )}

        {/* Contact welcome icon */}
        {message.type === "contact_welcome" && (
          <div className="flex items-center gap-2 mb-1">
            <Mail className={cn("h-4 w-4", highContrast ? "text-yellow-400" : "text-primary")} />
          </div>
        )}

        {/* Message content */}
        <p className={cn(
          "leading-relaxed whitespace-pre-wrap",
          largeText ? "text-base" : "text-sm"
        )}>{message.content}</p>

        {/* Main menu or options */}
        {(message.type === "options" || message.type === "main_menu") && message.options && isLatest && (
          <QuickReplyButtons options={message.options} onSelect={onOptionSelect} accessibility={accessibility} />
        )}

        {/* Product results */}
        {message.type === "products" && message.products && (
          <ProductResults products={message.products} />
        )}


        {/* Order number input hint */}
        {message.type === "request_order_number" && isLatest && (
          <div className={cn(
            "flex items-center gap-2 mt-2 text-xs",
            highContrast ? "text-gray-300" : "text-muted-foreground",
            largeText && "text-sm"
          )}>
            <Hash className="h-3 w-3" />
            <span>הקלד את מספר ההזמנה בשדה למטה</span>
          </div>
        )}

        {/* Phone input hint */}
        {message.type === "request_phone" && isLatest && (
          <div className={cn(
            "flex items-center gap-2 mt-2 text-xs",
            highContrast ? "text-gray-300" : "text-muted-foreground",
            largeText && "text-sm"
          )}>
            <Phone className="h-3 w-3" />
            <span>הקלד את מספר הטלפון בשדה למטה</span>
          </div>
        )}

        {/* OTP input hint */}
        {message.type === "request_otp" && isLatest && (
          <div className={cn(
            "flex items-center gap-2 mt-2 text-xs",
            highContrast ? "text-gray-300" : "text-muted-foreground",
            largeText && "text-sm"
          )}>
            <span>🔐 הקלד את קוד האימות בן 6 הספרות</span>
          </div>
        )}

        {/* Detailed order display */}
        {message.type === "order_detail" && message.detailedOrder && (
          <DetailedOrderView 
            order={message.detailedOrder} 
            highContrast={highContrast} 
            largeText={largeText}
            highlightLinks={highlightLinks}
          />
        )}

        {/* Detailed repair display */}
        {message.type === "order_detail" && message.detailedRepair && (
          <DetailedRepairView 
            repair={message.detailedRepair} 
            highContrast={highContrast} 
            largeText={largeText}
            highlightLinks={highlightLinks}
          />
        )}


        {/* WhatsApp link */}
        {message.type === "open_whatsapp" && message.whatsappUrl && (
          <a
            href={message.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center gap-2 mt-2 hover:underline",
              largeText ? "text-base" : "text-sm",
              highlightLinks 
                ? "bg-yellow-300 text-black px-1 rounded font-bold" 
                : highContrast ? "text-yellow-400" : "text-primary"
            )}
          >
            <img src={whatsappIcon} alt="WhatsApp" className="h-5 w-5" />
            פתח וואטסאפ
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </motion.div>
  );
}
